// The specialty catalogue for the FOREIGN onboarding (PM-0074).
//
// The list comes from the practitioner service's `specialties` table through
// the BFF's `specialties` query, labelled in Persian, English and Arabic (and
// French and German, see SPECIALTIES_QUERY) — it is not `constants/specialties.js`, which
// only translates a stored value for display. What the form SENDS is the row's `code`; the BFF stores that row's
// Persian name, so `doctor.specialty` stays Persian in every language.
//
// «سایر» (OTHER_SPECIALTY) opens a text box for a specialty the list does not
// name; that text is stored as typed.

import { compareFa } from '../../constants/specialties';

// `nameFr` and `nameDe` are NOT asked for yet: a BFF without the field fails the whole query
// (and empties the picker), so it joins the selection only once every
// environment runs a doctor BFF that declares them (docs/locale/FRENCH.md §3.4;
// German, PM-0212, the same). Until then a French or German reader sees the
// English names, which `specialtyRowLabel` already reads `nameFr` / `nameDe`
// ahead of.
export const SPECIALTIES_QUERY = `
  query Specialties {
    specialties { code kind nameFa nameEn nameAr sortOrder }
  }
`;

// Must match the BFF's OTHER_SPECIALTY_CODE.
export const OTHER_SPECIALTY = 'other';

export const SPECIALTY = 'specialty';
export const SUBSPECIALTY = 'subspecialty';

/**
 * The label of a catalogue row in the UI language — Persian as the fallback,
 * and English before it for a French or German reader (docs/locale/FRENCH.md D4).
 */
export const specialtyRowLabel = (row, locale) => {
  if (locale === 'en') return row.nameEn || row.nameFa;
  if (locale === 'ar') return row.nameAr || row.nameFa;
  if (locale === 'fr') return row.nameFr || row.nameEn || row.nameFa;
  if (locale === 'de') return row.nameDe || row.nameEn || row.nameFa;
  return row.nameFa;
};

/**
 * `[{ kind, rows: [{ code, label }] }]` — تخصص before فوق‌تخصص, each sorted in
 * the alphabet of the UI language. Persian uses `compareFa`, because ICU-less
 * runtimes sort «آ» after «ی».
 */
export function groupSpecialties(rows, locale) {
  const compare = locale === 'fa' || !locale
    ? (a, b) => compareFa(a.label, b.label)
    : (a, b) => a.label.localeCompare(b.label, locale);
  return [SPECIALTY, SUBSPECIALTY]
    .map((kind) => ({
      kind,
      rows: (rows || [])
        .filter((r) => (r.kind === SUBSPECIALTY ? SUBSPECIALTY : SPECIALTY) === kind)
        .map((r) => ({ code: r.code, label: specialtyRowLabel(r, locale) }))
        .sort(compare),
    }))
    .filter((g) => g.rows.length > 0);
}
